import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Radio, Wifi, WifiOff, HelpCircle, Server, Loader2, AlertTriangle,
    RefreshCw, MapPin, Clock, ShieldAlert, Cpu
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';

const POLL_MS = 45000;

const formatAgo = (value) => {
    if (!value) return null;
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return null;
    return dt.toLocaleString('tr-TR');
};

const StatCard = ({ tone, icon: Icon, label, value, hint, delay }) => {
    const tones = {
        green: {
            wrap: 'from-emerald-500/15 via-emerald-400/5 to-transparent dark:from-emerald-400/20 dark:via-emerald-500/5',
            ring: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-300',
            glow: 'shadow-emerald-500/20',
            num: 'text-emerald-600 dark:text-emerald-300'
        },
        red: {
            wrap: 'from-rose-500/15 via-rose-400/5 to-transparent dark:from-rose-400/20 dark:via-rose-500/5',
            ring: 'bg-rose-500/20 text-rose-600 dark:text-rose-300',
            glow: 'shadow-rose-500/20',
            num: 'text-rose-600 dark:text-rose-300'
        },
        amber: {
            wrap: 'from-amber-500/15 via-amber-400/5 to-transparent dark:from-amber-400/20 dark:via-amber-500/5',
            ring: 'bg-amber-500/20 text-amber-600 dark:text-amber-300',
            glow: 'shadow-amber-500/20',
            num: 'text-amber-600 dark:text-amber-300'
        }
    };
    const t = tones[tone];

    return (
        <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
            className={`relative overflow-hidden rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-800/80 shadow-lg ${t.glow} p-5`}
        >
            <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${t.wrap}`} />
            <div className="relative flex items-start justify-between">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">{label}</p>
                    <motion.p
                        key={value}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`mt-2 text-4xl font-black tabular-nums ${t.num}`}
                    >
                        {value}
                    </motion.p>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 max-w-[16rem]">{hint}</p>
                </div>
                <div className={`relative p-3 rounded-2xl ${t.ring}`}>
                    <span className={`absolute inset-0 rounded-2xl ${t.ring} animate-ping opacity-30`} />
                    <Icon className="relative w-6 h-6" />
                </div>
            </div>
        </motion.div>
    );
};

const DeviceRow = ({ item, tone, index }) => {
    const toneClass = {
        green: 'border-emerald-200/70 dark:border-emerald-500/20 hover:bg-emerald-50/70 dark:hover:bg-emerald-500/10',
        red: 'border-rose-200/70 dark:border-rose-500/20 hover:bg-rose-50/70 dark:hover:bg-rose-500/10',
        amber: 'border-amber-200/70 dark:border-amber-500/20 hover:bg-amber-50/70 dark:hover:bg-amber-500/10',
        slate: 'border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5'
    }[tone];

    const dot = {
        green: 'bg-emerald-500 shadow-emerald-400/80',
        red: 'bg-rose-500 shadow-rose-400/80',
        amber: 'bg-amber-500 shadow-amber-400/80',
        slate: 'bg-gray-400'
    }[tone];

    return (
        <motion.li
            initial={{ opacity: 0, x: tone === 'red' ? 12 : -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: Math.min(index * 0.04, 0.4) }}
            className={`rounded-xl border ${toneClass} p-3.5 transition-colors`}
        >
            <div className="flex items-start gap-3">
                <span className={`mt-1.5 h-2.5 w-2.5 rounded-full shadow ${dot} ${tone === 'green' ? 'animate-pulse' : ''}`} />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-gray-900 dark:text-white truncate">{item.deviceName}</p>
                        {item.brand && (
                            <span className="shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-500 dark:text-gray-300">
                                {item.brand}
                            </span>
                        )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                        {item.ipAddress && <span className="font-mono">{item.ipAddress}</span>}
                        {item.location && (
                            <span className="inline-flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {item.location}
                            </span>
                        )}
                        {item.lastSeenAt && (
                            <span className="inline-flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {formatAgo(item.lastSeenAt)}
                            </span>
                        )}
                    </div>
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{item.reason}</p>
                </div>
            </div>
        </motion.li>
    );
};

export default function DeviceReachabilityPage() {
    const { permissions, isAuthenticated } = useAuth();
    const canView = useMemo(
        () => permissions.includes('devices:view_list') || permissions.includes('settings:manage'),
        [permissions]
    );

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [showUnknown, setShowUnknown] = useState(true);
    const [showSkipped, setShowSkipped] = useState(false);

    const load = useCallback(async (silent = false) => {
        if (!canView) return;
        if (!silent) setLoading(true);
        else setRefreshing(true);
        setError('');
        try {
            const snap = await apiClient('DeviceReachability');
            setData(snap);
        } catch (err) {
            setError(err.payload?.message || err.message || 'Cihaz erişimleri alınamadı.');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [canView]);

    useEffect(() => {
        if (!isAuthenticated || !canView) return undefined;
        load(false);
        const id = setInterval(() => load(true), POLL_MS);
        return () => clearInterval(id);
    }, [isAuthenticated, canView, load]);

    if (!canView) {
        return (
            <div className="p-8">
                <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-6 rounded-2xl flex items-center space-x-3 max-w-2xl mx-auto">
                    <AlertTriangle className="w-6 h-6" />
                    <p className="font-medium">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
                </div>
            </div>
        );
    }

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center p-16">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    const reachable = data?.reachable || [];
    const unreachable = data?.unreachable || [];
    const unknown = data?.unknown || [];
    const skipped = data?.notMonitored || [];
    const gateways = data?.gateways || [];

    return (
        <div className="max-w-6xl mx-auto py-8 px-4 space-y-6">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative overflow-hidden rounded-3xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 shadow-xl"
            >
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-800 to-indigo-950" />
                <div className="pointer-events-none absolute -right-10 -top-16 h-56 w-56 rounded-full border border-white/10 animate-[ping_3s_linear_infinite]" />
                <div className="pointer-events-none absolute -right-4 top-6 h-40 w-40 rounded-full border border-emerald-400/20" />
                <div className="pointer-events-none absolute right-16 top-16 h-24 w-24 rounded-full bg-emerald-400/10 blur-2xl" />

                <div className="relative px-6 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <div className="relative p-3 rounded-2xl bg-white/10 backdrop-blur-md">
                            <span className="absolute inset-0 rounded-2xl bg-emerald-400/20 animate-ping" />
                            <Radio className="relative w-8 h-8 text-white" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white">Cihaz Erişimleri</h2>
                            <p className="text-slate-300 text-sm mt-1">
                                Gateway servislerinizin bildirdiği canlı erişim. Cloud cihazları taramaz; durum değişince güncellenir.
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => load(true)}
                        disabled={refreshing}
                        className="inline-flex items-center gap-2 self-start md:self-auto px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors disabled:opacity-60"
                    >
                        <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                        Yenile
                    </button>
                </div>
            </motion.div>

            {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2 text-red-600 dark:text-red-400">
                    <AlertTriangle className="w-5 h-5 shrink-0" />
                    <span className="text-sm font-medium">{error}</span>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                    tone="green"
                    icon={Wifi}
                    label="Ulaşılabilen"
                    value={data?.reachableCount ?? 0}
                    hint="Gateway LAN üzerinden cevap alan cihazlar"
                    delay={0.05}
                />
                <StatCard
                    tone="red"
                    icon={WifiOff}
                    label="Ulaşılamayan"
                    value={data?.unreachableCount ?? 0}
                    hint="Servis ayakta, cihaz cevap vermiyor"
                    delay={0.12}
                />
                <StatCard
                    tone="amber"
                    icon={HelpCircle}
                    label="Durum bilinmiyor"
                    value={data?.unknownCount ?? 0}
                    hint="Servis çevrimdışı veya henüz rapor yok"
                    delay={0.19}
                />
            </div>

            <motion.section
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 p-5 shadow-sm"
            >
                <div className="flex items-center gap-2 mb-4">
                    <Server className="w-5 h-5 text-indigo-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">Gateway servisleri</h3>
                    <span className="text-xs text-gray-400">
                        {data?.gatewayOnline ?? 0} çevrimiçi / {data?.gatewayOffline ?? 0} çevrimdışı
                    </span>
                </div>
                {gateways.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Bu firmaya ait aktif gateway kaydı yok.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {gateways.map((g) => (
                            <div
                                key={g.serviceId}
                                className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 dark:border-white/10 px-4 py-3 bg-gray-50/70 dark:bg-white/5"
                            >
                                <div className="min-w-0">
                                    <p className="font-medium text-gray-900 dark:text-white truncate">
                                        {g.vendor} {g.sourceName ? `· ${g.sourceName}` : ''}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">{g.serviceId}</p>
                                    {g.machineName && (
                                        <p className="text-xs text-gray-400 inline-flex items-center gap-1 mt-0.5">
                                            <Cpu className="w-3 h-3" /> {g.machineName}
                                        </p>
                                    )}
                                </div>
                                <span className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                    g.online
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                        : 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                                }`}>
                                    <span className={`h-1.5 w-1.5 rounded-full ${g.online ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
                                    {g.online ? 'Çevrimiçi' : 'Çevrimdışı'}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </motion.section>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <section className="rounded-2xl border border-emerald-200/60 dark:border-emerald-500/20 bg-white dark:bg-gray-800 p-5 shadow-sm">
                    <h3 className="font-semibold text-emerald-700 dark:text-emerald-300 mb-4">Ulaşılabilen cihazlar</h3>
                    <ul className="space-y-2.5">
                        <AnimatePresence>
                            {reachable.length === 0 ? (
                                <p className="text-sm text-gray-500 dark:text-gray-400">Şu an yeşil cihaz yok.</p>
                            ) : reachable.map((item, i) => (
                                <DeviceRow key={item.id} item={item} tone="green" index={i} />
                            ))}
                        </AnimatePresence>
                    </ul>
                </section>
                <section className="rounded-2xl border border-rose-200/60 dark:border-rose-500/20 bg-white dark:bg-gray-800 p-5 shadow-sm">
                    <h3 className="font-semibold text-rose-700 dark:text-rose-300 mb-4">Ulaşılamayan cihazlar</h3>
                    <ul className="space-y-2.5">
                        {unreachable.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">Ulaşılamayan LAN cihazı yok.</p>
                        ) : unreachable.map((item, i) => (
                            <DeviceRow key={item.id} item={item} tone="red" index={i} />
                        ))}
                    </ul>
                </section>
            </div>

            <section className="rounded-2xl border border-amber-200/60 dark:border-amber-500/20 bg-white dark:bg-gray-800 p-5 shadow-sm">
                <button
                    type="button"
                    onClick={() => setShowUnknown(v => !v)}
                    className="w-full flex items-center justify-between text-left"
                >
                    <span className="inline-flex items-center gap-2 font-semibold text-amber-700 dark:text-amber-300">
                        <ShieldAlert className="w-4 h-4" /> Durumu bilinmeyen ({unknown.length})
                    </span>
                    <span className="text-xs text-gray-400">{showUnknown ? 'Gizle' : 'Göster'}</span>
                </button>
                {showUnknown && (
                    <ul className="mt-4 space-y-2.5">
                        {unknown.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">Bilinmeyen cihaz yok.</p>
                        ) : unknown.map((item, i) => (
                            <DeviceRow key={item.id} item={item} tone="amber" index={i} />
                        ))}
                    </ul>
                )}
            </section>

            {skipped.length > 0 && (
                <section className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-800 p-5 shadow-sm">
                    <button
                        type="button"
                        onClick={() => setShowSkipped(v => !v)}
                        className="w-full flex items-center justify-between text-left"
                    >
                        <span className="font-semibold text-gray-700 dark:text-gray-200">
                            İzlenmeyen cihazlar ({skipped.length})
                        </span>
                        <span className="text-xs text-gray-400">{showSkipped ? 'Gizle' : 'Göster'}</span>
                    </button>
                    {showSkipped && (
                        <ul className="mt-4 space-y-2.5">
                            {skipped.map((item, i) => (
                                <DeviceRow key={item.id} item={item} tone="slate" index={i} />
                            ))}
                        </ul>
                    )}
                </section>
            )}
        </div>
    );
}
